import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { HeroDeck } from '../components/hero/HeroDeck';
import { ContactForm } from '../components/ContactForm';
import { PROJECTS, SKILLS, SOCIAL_LINKS, type Project } from '../constants';
import { CASE_STUDIES } from '../data/caseStudies';
import { useLocale } from '../i18n';
import { caseStudyPath } from '../seo/routes';
import { ProjectGlyph, ExternalIcon } from '../components/icons';

const SERVICE_PROOF: Partial<Record<'business' | 'hospitality' | 'apps' | 'care', string>> = {
  hospitality: 'tipsytruck',
  apps: 'ai-ui-generator',
};

export const Landing = () => {
  const { t } = useTranslation();
  const locale = useLocale();

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />

      <main>
        <HeroDeck />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <section id="services" className="py-16 md:py-24">
            <div>
              <p className="eyebrow module-eyebrow mb-4 flex items-center gap-3">
                <span className="h-px w-8 bg-primary" aria-hidden="true" />
                01 / {t('services.eyebrow')}
              </p>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {t('services.title')}
              </h2>
              <p className="text-muted-foreground mb-12 max-w-2xl">
                {t('services.subtitle')}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {(['business', 'hospitality', 'apps', 'care'] as const).map((key, i) => (
                  <div key={key} className="tile" data-reveal>
                    <div className="tile-in flex flex-col !p-6">
                      <div className="mb-3 font-mono text-xs text-muted-foreground">
                        {String(i + 1).padStart(2, '0')}
                      </div>
                      <h3 className="mb-2 text-lg font-semibold">{t(`services.items.${key}.title`)}</h3>
                      <p className="text-sm text-muted-foreground">{t(`services.items.${key}.description`)}</p>
                      {SERVICE_PROOF[key] ? (
                        <Link
                          to={caseStudyPath(locale, SERVICE_PROOF[key])}
                          viewTransition
                          className="eyebrow -mx-2 -mb-3 mt-auto self-start px-2 pb-3 pt-4 text-primary transition-colors hover:text-foreground"
                        >
                          {t(`services.items.${key}.proof`)} <span aria-hidden="true">→</span>
                        </Link>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="projects" className="py-16 md:py-24">
            <div>
              <p className="eyebrow module-eyebrow mb-4 flex items-center gap-3">
                <span className="h-px w-8 bg-primary" aria-hidden="true" />
                02 / {t('nav.projects')}
              </p>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {t('projects.title')}
              </h2>
              <p className="text-muted-foreground mb-12">
                {t('projects.subtitle')}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {PROJECTS.filter((p) => p.featured).map((project) => (
                  <ProjectCard key={project.id} project={project} />
                ))}
              </div>
            </div>
          </section>

          <section id="about" className="py-16 md:py-24">
            <div>
              <p className="eyebrow module-eyebrow mb-4 flex items-center gap-3">
                <span className="h-px w-8 bg-primary" aria-hidden="true" />
                03 / {t('nav.about')}
              </p>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {t('about.title')}
              </h2>
              <p className="text-muted-foreground mb-10 max-w-2xl">
                {t('about.description')}
              </p>

              {/* Career path — the real route from law to code to Zürich */}
              <div className="mb-12 max-w-2xl" data-reveal>
                <p className="eyebrow mb-5 text-muted-foreground">{t('about.journey.title')}</p>
                <ol className="space-y-3">
                  {(['law', 'pivot', 'agency', 'saas', 'now'] as const).map((k) => (
                    <li
                      key={k}
                      className={`flex flex-wrap items-baseline gap-x-4 gap-y-0.5 border-l-2 pl-4 ${
                        k === 'now' ? 'border-primary' : 'border-border'
                      }`}
                    >
                      <span className="font-mono text-xs tracking-wide text-primary min-w-[92px]">
                        {t(`about.journey.${k}.when`)}
                      </span>
                      <span className="text-sm text-foreground">{t(`about.journey.${k}.what`)}</span>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <SkillCard
                  title={t('about.skills.backend')}
                  skills={SKILLS.backend}
                  colorClass="primary"
                />

                <SkillCard
                  title={t('about.skills.frontend')}
                  skills={SKILLS.frontend}
                  colorClass="accent"
                />

                <SkillCard
                  title={t('about.skills.devops')}
                  skills={SKILLS.devops}
                  colorClass="secondary"
                />
              </div>
            </div>
          </section>

          <section id="how-we-start" className="py-16 md:py-24">
            <div>
              <p className="eyebrow module-eyebrow mb-4 flex items-center gap-3">
                <span className="h-px w-8 bg-primary" aria-hidden="true" />
                04 / {t('howwestart.eyebrow')}
              </p>
              <h2 className="text-3xl md:text-4xl font-bold mb-12 max-w-2xl">{t('howwestart.title')}</h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {(['brief', 'prototype', 'build', 'ship'] as const).map((k, i) => (
                  <div key={k} className="tile" data-reveal>
                    <div className="tile-in !p-6">
                      <div className="mb-3 font-mono text-xs text-primary">{String(i + 1).padStart(2, '0')}</div>
                      <h3 className="mb-2 font-mono text-sm font-semibold uppercase tracking-wide">
                        {t(`howwestart.steps.${k}.title`)}
                      </h3>
                      <p className="text-sm text-muted-foreground">{t(`howwestart.steps.${k}.body`)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="contact" className="py-16 md:py-24">
            <div>
              <div className="mb-8 flex flex-wrap items-end justify-between gap-6">
                <div>
                  <p className="eyebrow module-eyebrow mb-4 flex items-center gap-3">
                    <span className="h-px w-8 bg-primary" aria-hidden="true" />
                    05 / {t('common.contact')}
                  </p>
                  <h2 className="text-3xl md:text-4xl mb-4">{t('contact.title')}</h2>
                  <p className="text-muted-foreground max-w-xl">{t('contact.description')}</p>
                </div>
                <div className="eyebrow flex flex-wrap gap-6">
                  <a href={SOCIAL_LINKS.linkedin} target="_blank" rel="noreferrer" className="transition-colors hover:text-primary">
                    LinkedIn ↗
                  </a>
                  <a href={SOCIAL_LINKS.github} target="_blank" rel="noreferrer" className="transition-colors hover:text-primary">
                    GitHub ↗
                  </a>
                </div>
              </div>
              <div className="tile">
                <div className="tile-in !p-6 sm:!p-8">
                  <ContactForm />
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

interface SkillCardProps {
  title: string;
  skills: readonly string[];
  colorClass: 'primary' | 'secondary' | 'accent';
}

const SkillCard = ({ title, skills, colorClass }: SkillCardProps) => {
  const badgeClass = {
    primary: 'badge-primary',
    secondary: 'badge-secondary',
    accent: 'badge-accent',
  };

  return (
    <div className="tile" data-reveal>
      <div className="tile-in !p-6">
        <h3 className="font-semibold text-lg mb-4">{title}</h3>
        <div className="flex flex-wrap gap-2">
          {skills.map((skill) => (
            <span key={skill} className={`badge ${badgeClass[colorClass]}`}>
              {skill}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

interface ProjectCardProps {
  project: Project;
}

const statusColors: Record<Project['status'], string> = {
  live: 'badge-primary',
  concept: 'badge-secondary',
  development: 'badge-secondary',
  'coming-soon': 'badge-secondary',
};

const ProjectCard = ({ project }: ProjectCardProps) => {
  const { t } = useTranslation();
  const locale = useLocale();
  const item = `projects.items.${project.slug}`;
  const studyHref = CASE_STUDIES[project.slug] ? caseStudyPath(locale, project.slug) : undefined;

  const body = (
    <>
      <div className="mb-4 flex items-start justify-between">
        <span className="glyph-chip h-12 w-12">
          <span>
            <ProjectGlyph slug={project.slug} className="h-6 w-6 text-primary" />
          </span>
        </span>
        <span className={`badge ${statusColors[project.status]}`}>{t(`projects.status.${project.status}`)}</span>
      </div>

      <h3 className="mb-2 text-xl font-semibold transition-colors group-hover:text-primary">{t(`${item}.title`)}</h3>
      <p className="mb-4 max-w-[42ch] text-sm text-muted-foreground">{t(`${item}.description`)}</p>

      {project.outcome ? <p className="mb-4 font-mono text-xs text-primary">{t(`${item}.outcome`)}</p> : null}

      <div className="flex flex-wrap gap-2">
        {project.tags.map((tag) => (
          <span key={tag} className="badge badge-accent">
            {tag}
          </span>
        ))}
      </div>
    </>
  );

  const ctaClass =
    'mt-auto flex items-center gap-1 pt-6 text-sm leading-5 text-muted-foreground transition-colors group-hover:text-primary';
  const cardClass = 'group tile-in relative z-10 flex flex-col !p-6';

  if (project.url) {
    return (
      <div className="tile tile-hover relative h-full" data-reveal>
        <a href={project.url} target="_blank" rel="noreferrer" className={cardClass}>
          {body}
          <div className={ctaClass}>
            <span>{t('projects.cta.visit')}</span>
            <ExternalIcon className="h-4 w-4" />
            <span className="sr-only">({t('projects.cta.newTab')})</span>
          </div>
        </a>

        {studyHref ? (
          <Link
            to={studyHref}
            viewTransition
            className="eyebrow absolute bottom-[13px] right-[13px] z-20 inline-flex items-center gap-1 p-3 leading-5 transition-colors hover:text-primary"
          >
            {t('projects.cta.caseStudy')} <span aria-hidden="true">→</span>
          </Link>
        ) : null}
      </div>
    );
  }

  if (!studyHref) throw new Error(`Project ${project.slug} has neither a live URL nor a case study`);

  return (
    <div className="tile tile-hover relative h-full" data-reveal>
      <Link to={studyHref} viewTransition className={cardClass}>
        {body}
        <div className={ctaClass}>
          <span>{t(project.demo ? 'projects.cta.demo' : 'projects.cta.caseStudy')}</span>
          <span aria-hidden="true">→</span>
        </div>
      </Link>
    </div>
  );
};

export default Landing;
